import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/ApiService';

const CALL_STATE_COLORS = {
  answered: '#10b981',
  missed:   '#ef4444',
  busy:     '#f59e0b',
  started:  '#6366f1',
  ended:    '#6b7280',
};

function timeAgo(dateStr) {
  if (!dateStr) return '';
  const diff = Date.now() - new Date(dateStr).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function fmtDuration(s) {
  if (!s) return '';
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

function fmtDate(v) {
  if (!v) return '';
  return new Date(v).toLocaleString();
}

export default function CrmSidePanel({ phone, customer: initialCustomer, callUuid, onDial }) {
  const [customer, setCustomer] = useState(initialCustomer || null);
  const [timeline, setTimeline] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [callNote, setCallNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [newTaskTitle, setNewTaskTitle] = useState('');
  const [newTaskDue, setNewTaskDue] = useState('');
  const [loadingTimeline, setLoadingTimeline] = useState(false);

  // Refresh customer + timeline when customer changes
  const loadTimeline = useCallback(async (id) => {
    setLoadingTimeline(true);
    try {
      const data = await api.getCustomerTimeline(id);
      setTimeline(data || []);
    } catch (e) {
      console.error('Failed to load timeline', e);
    } finally {
      setLoadingTimeline(false);
    }
  }, []);

  const loadTasks = useCallback(async (id) => {
    try {
      const data = await api.getCrmTasks(id);
      setTasks(data || []);
    } catch (e) {
      console.error('Failed to load tasks', e);
    }
  }, []);

  useEffect(() => {
    setCustomer(initialCustomer || null);
    if (initialCustomer?.id) {
      loadTimeline(initialCustomer.id);
      loadTasks(initialCustomer.id);
    } else {
      setTimeline([]);
      setTasks([]);
    }
    setCallNote('');
    setNoteSaved(false);
  }, [initialCustomer, loadTimeline, loadTasks]);

  const autoCreate = async () => {
    const name = newName.trim() || phone;
    setCreating(true);
    try {
      const result = await api.autoCreateCrmCustomer({ phone, name, call_uuid: callUuid });
      const cust = result.customer || result;
      setCustomer(cust);
      if (cust?.id) {
        await loadTimeline(cust.id);
        await loadTasks(cust.id);
      }
      setNewName('');
    } catch (e) {
      console.error('Failed to create customer', e);
      alert('Failed to create customer record.');
    } finally {
      setCreating(false);
    }
  };

  const saveCallNote = async () => {
    const note = callNote.trim();
    if (!note) return;

    setNoteSaving(true);
    try {
      await api.saveCrmCallNote({
        customer_id: customer?.id || null,
        call_uuid: callUuid || null,
        phone,
        note,
      });
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 3000);
      if (customer?.id) {
        await loadTimeline(customer.id);
      }
    } catch (e) {
      console.error('Failed to save note', e);
      alert('Failed to save note.');
    } finally {
      setNoteSaving(false);
    }
  };

  const addTask = async () => {
    const title = newTaskTitle.trim();
    if (!title || !customer?.id) return;
    try {
      await api.createCrmTask({
        customer_id: customer.id,
        title,
        due_date: newTaskDue || null,
      });
      setNewTaskTitle('');
      setNewTaskDue('');
      await loadTasks(customer.id);
    } catch (e) {
      console.error('Failed to add task', e);
    }
  };

  const completeTask = async (task) => {
    try {
      await api.updateCrmTask(task.id, { status: 'done' });
      await loadTasks(customer.id);
    } catch (e) {
      console.error('Failed to complete task', e);
    }
  };

  const openTasks = tasks.filter(t => t.status !== 'done');

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      background: '#fff',
      borderLeft: '1px solid #e5e7eb',
      fontSize: '13px',
      overflow: 'hidden',
    }}>
      {/* Header */}
      <div style={{
        padding: '14px 16px 10px',
        borderBottom: '1px solid #e5e7eb',
        background: '#f8fafc',
        flexShrink: 0,
      }}>
        {customer ? (
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <div style={{
              width: 40, height: 40, borderRadius: '50%',
              background: '#6366f1', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', fontWeight: 700, flexShrink: 0,
            }}>
              {(customer.name || phone || '?')[0].toUpperCase()}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: '15px', color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {customer.name}
              </div>
              {customer.company && (
                <div style={{ color: '#6b7280', fontSize: '12px' }}>{customer.company}</div>
              )}
              <div style={{ color: '#6b7280', fontSize: '12px', marginTop: 2 }}>{phone}</div>
            </div>
            {onDial && customer && (
              <button
                onClick={() => onDial(phone)}
                style={{
                  background: '#10b981', color: '#fff', border: 'none',
                  borderRadius: '8px', padding: '5px 12px', fontSize: '12px',
                  cursor: 'pointer', fontWeight: 600, flexShrink: 0,
                }}
              >
                Dial
              </button>
            )}
          </div>
        ) : (
          <div>
            <div style={{ fontWeight: 600, color: '#111827', marginBottom: 4 }}>Unknown Caller</div>
            <div style={{ color: '#6b7280', fontSize: '12px' }}>{phone}</div>
          </div>
        )}
      </div>

      {/* Unknown caller — create record */}
      {!customer && (
        <div style={{
          padding: '12px 16px',
          background: '#fefce8',
          borderBottom: '1px solid #fde68a',
          flexShrink: 0,
        }}>
          <div style={{ fontSize: '12px', color: '#92400e', marginBottom: '8px', fontWeight: 500 }}>
            No CRM record found for this number.
          </div>
          <input
            value={newName}
            onChange={e => setNewName(e.target.value)}
            placeholder="Customer name (optional)"
            style={{
              width: '100%', padding: '6px 10px', borderRadius: '8px',
              border: '1px solid #d1d5db', fontSize: '13px', marginBottom: '8px', boxSizing: 'border-box',
            }}
          />
          <button
            onClick={autoCreate}
            disabled={creating}
            style={{
              background: '#6366f1', color: '#fff', border: 'none',
              borderRadius: '8px', padding: '6px 14px', fontSize: '12px',
              cursor: creating ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: creating ? 0.7 : 1,
            }}
          >
            {creating ? 'Creating…' : '+ Create CRM Record'}
          </button>
        </div>
      )}

      {/* Tabs */}
      <div style={{
        display: 'flex', borderBottom: '1px solid #e5e7eb',
        flexShrink: 0, background: '#fff',
      }}>
        {['overview', 'timeline', 'tasks'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            style={{
              flex: 1, padding: '8px 4px',
              background: activeTab === tab ? '#6366f1' : 'transparent',
              color: activeTab === tab ? '#fff' : '#6b7280',
              border: 'none', fontSize: '12px', cursor: 'pointer',
              fontWeight: activeTab === tab ? 700 : 400,
              transition: 'all 0.15s',
              textTransform: 'capitalize',
            }}
          >
            {tab}
            {tab === 'tasks' && openTasks.length > 0 && (
              <span style={{
                marginLeft: 5, background: activeTab === 'tasks' ? '#fff' : '#ef4444',
                color: activeTab === 'tasks' ? '#6366f1' : '#fff',
                borderRadius: '999px', padding: '1px 6px', fontSize: '10px',
              }}>
                {openTasks.length}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '12px 16px' }}>

        {/* OVERVIEW */}
        {activeTab === 'overview' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
            {customer && (
              <div style={{ display: 'grid', gap: '6px' }}>
                {customer.email && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ color: '#6b7280', minWidth: 60 }}>Email</span>
                    <span style={{ color: '#111827', fontWeight: 500 }}>{customer.email}</span>
                  </div>
                )}
                {customer.phone && (
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <span style={{ color: '#6b7280', minWidth: 60 }}>Phone</span>
                    <span style={{ color: '#111827' }}>{customer.phone}</span>
                  </div>
                )}
                {customer.notes && (
                  <div>
                    <div style={{ color: '#6b7280', marginBottom: 4 }}>Notes</div>
                    <div style={{
                      background: '#f8fafc', borderRadius: '8px', padding: '8px 10px',
                      color: '#374151', fontSize: '12px', lineHeight: 1.5,
                    }}>
                      {customer.notes}
                    </div>
                  </div>
                )}
                {customer.tags && customer.tags.length > 0 && (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginTop: 4 }}>
                    {customer.tags.map(tag => (
                      <span key={tag.id || tag} style={{
                        background: '#ede9fe', color: '#7c3aed',
                        borderRadius: '999px', padding: '2px 10px', fontSize: '11px',
                      }}>
                        {tag.name || tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Recent calls summary (up to 3) */}
            {customer && (
              <>
                <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
                  Recent Calls
                </div>
                {timeline
                  .filter(t => t.type === 'call')
                  .slice(0, 3)
                  .map((item, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 10px', background: '#f8fafc', borderRadius: '10px',
                    }}>
                      <span style={{
                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                        background: CALL_STATE_COLORS[item.status] || '#6b7280',
                      }} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: '#111827', fontWeight: 500 }}>
                          {item.direction === 'inbound' ? 'Inbound' : 'Outbound'}
                          {item.duration ? ` · ${fmtDuration(item.duration)}` : ''}
                        </div>
                        <div style={{ color: '#9ca3af', fontSize: '11px' }}>{timeAgo(item.created_at || item.call_start)}</div>
                      </div>
                    </div>
                  ))}
                {timeline.filter(t => t.type === 'call').length === 0 && (
                  <div style={{ color: '#9ca3af', fontSize: '12px' }}>No previous calls.</div>
                )}
              </>
            )}

            {/* Call note */}
            <div>
              <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginBottom: 8 }}>
                Call Note
              </div>
              <textarea
                value={callNote}
                onChange={e => setCallNote(e.target.value)}
                placeholder="Add a note about this call…"
                rows={3}
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '8px 10px',
                  border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '13px',
                  resize: 'vertical', fontFamily: 'inherit',
                }}
              />
              <button
                onClick={saveCallNote}
                disabled={!callNote.trim() || noteSaving}
                style={{
                  marginTop: '6px',
                  background: noteSaved ? '#10b981' : '#6366f1',
                  color: '#fff', border: 'none', borderRadius: '8px',
                  padding: '6px 16px', fontSize: '12px',
                  cursor: !callNote.trim() || noteSaving ? 'not-allowed' : 'pointer',
                  opacity: !callNote.trim() ? 0.5 : 1,
                  fontWeight: 600,
                  transition: 'background 0.2s',
                }}
              >
                {noteSaved ? 'Saved!' : noteSaving ? 'Saving…' : 'Save Note'}
              </button>
            </div>
          </div>
        )}

        {/* TIMELINE */}
        {activeTab === 'timeline' && (
          <div>
            {loadingTimeline && <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>Loading…</div>}
            {!loadingTimeline && timeline.length === 0 && (
              <div style={{ color: '#9ca3af', textAlign: 'center', padding: '20px' }}>No interactions yet.</div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {timeline.map((item, i) => (
                <div key={i} style={{
                  padding: '10px 12px',
                  background: '#f8fafc',
                  borderRadius: '10px',
                  borderLeft: `3px solid ${
                    item.type === 'call' ? (CALL_STATE_COLORS[item.status] || '#6b7280') :
                    item.type === 'note' || item.type === 'call_note' ? '#6366f1' :
                    item.type === 'task' ? '#f59e0b' : '#6b7280'
                  }`,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <span style={{ fontWeight: 600, color: '#374151', textTransform: 'capitalize', fontSize: '12px' }}>
                      {item.type === 'call_note' ? 'Call Note' : item.type}
                      {item.type === 'call' && item.direction && ` · ${item.direction}`}
                    </span>
                    <span style={{ color: '#9ca3af', fontSize: '11px' }}>
                      {timeAgo(item.created_at || item.call_start)}
                    </span>
                  </div>
                  {item.type === 'call' && (
                    <div style={{ color: '#6b7280', fontSize: '12px' }}>
                      {item.status || 'unknown'}{item.duration ? ` · ${fmtDuration(item.duration)}` : ''}
                    </div>
                  )}
                  {(item.type === 'note' || item.type === 'call_note') && item.note && (
                    <div style={{ color: '#374151', fontSize: '12px', lineHeight: 1.5 }}>{item.note}</div>
                  )}
                  {item.type === 'task' && (
                    <div style={{ color: '#374151', fontSize: '12px' }}>
                      {item.title}
                      {item.status === 'done' && <span style={{ color: '#10b981', marginLeft: 8 }}>Done</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* TASKS */}
        {activeTab === 'tasks' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {!customer && (
              <div style={{ color: '#9ca3af', fontSize: '12px' }}>Create a CRM record first to add tasks.</div>
            )}

            {customer && (
              <>
                {/* Open tasks */}
                {openTasks.length === 0 && (
                  <div style={{ color: '#9ca3af', fontSize: '12px', textAlign: 'center', padding: '12px 0' }}>No open tasks.</div>
                )}
                {openTasks.map(task => (
                  <div key={task.id} style={{
                    display: 'flex', alignItems: 'flex-start', gap: '10px',
                    padding: '10px 12px', background: '#fefce8', borderRadius: '10px',
                    border: '1px solid #fde68a',
                  }}>
                    <button
                      onClick={() => completeTask(task)}
                      style={{
                        width: 20, height: 20, borderRadius: '50%', border: '2px solid #d1d5db',
                        background: '#fff', cursor: 'pointer', flexShrink: 0, marginTop: 1,
                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0,
                      }}
                      title="Mark done"
                    >
                      <span style={{ fontSize: '10px', color: '#6b7280' }}>✓</span>
                    </button>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 500, color: '#111827', fontSize: '13px' }}>{task.title}</div>
                      {task.due_date && (
                        <div style={{ color: '#9ca3af', fontSize: '11px', marginTop: 2 }}>
                          Due: {fmtDate(task.due_date)}
                        </div>
                      )}
                    </div>
                  </div>
                ))}

                {/* Add task */}
                <div style={{
                  padding: '10px 12px', background: '#f8fafc', borderRadius: '10px',
                  border: '1px solid #e5e7eb',
                }}>
                  <input
                    value={newTaskTitle}
                    onChange={e => setNewTaskTitle(e.target.value)}
                    placeholder="Task title…"
                    style={{
                      width: '100%', padding: '6px 10px', borderRadius: '8px',
                      border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box',
                      marginBottom: '6px',
                    }}
                    onKeyDown={e => e.key === 'Enter' && addTask()}
                  />
                  <input
                    type="datetime-local"
                    value={newTaskDue}
                    onChange={e => setNewTaskDue(e.target.value)}
                    style={{
                      width: '100%', padding: '6px 10px', borderRadius: '8px',
                      border: '1px solid #d1d5db', fontSize: '12px', boxSizing: 'border-box',
                      marginBottom: '8px',
                    }}
                  />
                  <button
                    onClick={addTask}
                    disabled={!newTaskTitle.trim()}
                    style={{
                      background: '#6366f1', color: '#fff', border: 'none',
                      borderRadius: '8px', padding: '6px 14px', fontSize: '12px',
                      cursor: newTaskTitle.trim() ? 'pointer' : 'not-allowed',
                      opacity: newTaskTitle.trim() ? 1 : 0.5, fontWeight: 600,
                    }}
                  >
                    + Add Task
                  </button>
                </div>

                {/* Completed tasks */}
                {tasks.filter(t => t.status === 'done').length > 0 && (
                  <>
                    <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600, marginTop: 4 }}>
                      Completed
                    </div>
                    {tasks.filter(t => t.status === 'done').map(task => (
                      <div key={task.id} style={{
                        padding: '8px 12px', background: '#f8fafc', borderRadius: '10px',
                        color: '#9ca3af', textDecoration: 'line-through', fontSize: '12px',
                      }}>
                        {task.title}
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
